import { useState } from "react";
import { Calendar, Check, ChevronDown, MapPin } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import type { GlobalFilters } from "@cd/core";
import { api } from "../../lib/api";
import { asSelection, customRange, describeRange, iso, presetLabel, presetRange, TIME_PRESETS } from "../../lib/time";
import { cn } from "../../lib/utils";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/primitives";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/overlay";

interface Props {
  /** The dashboard's saved date range. */
  filters: GlobalFilters;
  onChange: (f: GlobalFilters) => void;
  /** This viewer's location pick, never saved with the dashboard. Undefined means all of theirs. */
  locationIds: string[] | undefined;
  onLocationsChange: (ids: string[] | undefined) => void;
  /** A date zoom is showing other dates than the saved range, so the picker says so. */
  zoomed?: boolean;
}

export function FilterBar({ filters, onChange, locationIds: selected, onLocationsChange, zoomed }: Props) {
  const locations = useQuery({ queryKey: ["locations"], queryFn: api.locations, staleTime: 10 * 60_000 });
  const time = asSelection(filters);
  const range = time.timeRange ?? presetRange(time.timePreset);
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState<{ first: string; last: string } | null>(null);
  const openCustom = () => setCustom({ first: range.start.slice(0, 10), last: range.end.slice(0, 10) });
  const today = iso(new Date());
  const all = locations.data ?? [];
  const locLabel = !selected?.length || selected.length === all.length
    ? all.length === 1 ? all[0]!.name : `All locations${all.length ? ` (${all.length})` : ""}`
    : selected.length === 1 ? all.find((l) => l.id === selected[0])?.name ?? "1 location" : `${selected.length} locations`;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Popover
        open={open}
        onOpenChange={(o) => {
          setOpen(o);
          setCustom(o && time.timeRange ? { first: time.timeRange.start.slice(0, 10), last: time.timeRange.end.slice(0, 10) } : null);
        }}
      >
        <PopoverTrigger asChild>
          <Button size="md">
            <Calendar className="size-3.5 text-subtle" />
            {time.timeRange ? "Custom" : presetLabel(time.timePreset)}
            <span className="text-subtle">{describeRange(range)}{zoomed && " (zoomed)"}</span>
            <ChevronDown className="size-3.5 text-subtle" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-64">
          {TIME_PRESETS.map((p) => (
            <button
              key={p.id}
              onClick={() => {
                onChange({ ...filters, timePreset: p.id, timeRange: undefined });
                setOpen(false);
              }}
              className="flex w-full items-center justify-between rounded-lg px-2 py-1.5 text-sm hover:bg-surface-2"
            >
              {p.label}
              {p.id === time.timePreset && <Check className="size-4 text-foreground" strokeWidth={2.5} />}
            </button>
          ))}
          <div className="my-1 h-px bg-border" />
          <button
            onClick={() => (custom ? setCustom(null) : openCustom())}
            className="flex w-full items-center justify-between rounded-lg px-2 py-1.5 text-sm hover:bg-surface-2"
          >
            Custom range…
            {time.timeRange && <Check className="size-4 text-foreground" strokeWidth={2.5} />}
          </button>
          {custom && (
            <form
              className="space-y-2 px-2 pt-1 pb-2"
              onSubmit={(e) => {
                e.preventDefault();
                if (!custom.first || !custom.last) return;
                onChange({ ...filters, timePreset: undefined, timeRange: customRange(custom.first, custom.last) });
                setOpen(false);
              }}
            >
              <label className="flex items-center justify-between gap-2 text-xs text-subtle">
                From
                <Input type="date" required max={today} value={custom.first} onChange={(e) => setCustom({ ...custom, first: e.target.value })} className="w-40" />
              </label>
              <label className="flex items-center justify-between gap-2 text-xs text-subtle">
                To
                <Input type="date" required min={custom.first} max={today} value={custom.last} onChange={(e) => setCustom({ ...custom, last: e.target.value })} className="w-40" />
              </label>
              <p className="text-xs text-subtle">Same dates for every location, both days included.</p>
              <Button type="submit" variant="primary" className="w-full justify-center" disabled={!custom.first || !custom.last}>
                Apply
              </Button>
            </form>
          )}
        </PopoverContent>
      </Popover>

      {all.length > 1 && (
        <Popover>
          <PopoverTrigger asChild>
            <Button>
              <MapPin className="size-3.5 text-subtle" />
              {locLabel}
              <ChevronDown className="size-3.5 text-subtle" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64">
            <button
              onClick={() => onLocationsChange(undefined)}
              className="flex w-full items-center justify-between rounded-lg px-2 py-1.5 text-sm hover:bg-surface-2"
            >
              All locations {!selected?.length && <Check className="size-4" strokeWidth={2.5} />}
            </button>
            <div className="my-1 h-px bg-border" />
            {all.map((l) => {
              const on = !!selected?.includes(l.id);
              return (
                <button
                  key={l.id}
                  onClick={() => {
                    const next = on ? selected!.filter((x) => x !== l.id) : [...(selected ?? []), l.id];
                    onLocationsChange(next.length ? next : undefined);
                  }}
                  className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-surface-2"
                >
                  <span className={cn("flex size-4 items-center justify-center rounded border", on ? "border-accent bg-accent text-white" : "border-border-strong")}>
                    {on && <Check className="size-3" strokeWidth={3} />}
                  </span>
                  {l.name}
                </button>
              );
            })}
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
